// ---------------------------------------------------------------------------
// Sonda da LATERAL (Preço x Força) e, como controle, do CHÃO. Só diagnóstico, só com "?dbg=1".
//
// Pergunta: por que a lateral pisca na navegação histórica se o filamento, os candles e o chão estão fluidos?
// A lateral é a única linha que é recortada A CADA QUADRO por setDrawRange (busca binária no X dos pontos);
// o chão desenha o lote inteiro e é recortado por planos.
//
// Como mede (sem alterar nada do app): imediatamente antes de cada linha ser desenhada (onBeforeRender),
// compara o drawRange que vai para a GPU com o drawRange esperado, recalculado de forma independente sobre a
// CÓPIA que está na GPU (o array do atributo de posição do próprio FastLine), com a mesma janela visível.
// Também registra: total de vértices, versões dos atributos de posição e cor, material (objeto) e a flag de cor
// por vértice, remontagem da instância, tracejado sem distâncias e quadros em que a linha não foi desenhada.
// Zero alocação por quadro: só números e referências em campos/arrays pré-alocados.
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';
import { jank, NEV, EV_NAMES } from './jankRecorder';
import { SMODE_NAMES, NSM } from './syncProbe';

/** Linhas sondadas (posição fixa). 0-3 lateral, 4-7 chão. */
export const SLOT_NAMES = ['lateral', 'lateral SMA10', 'lateral SMA20', 'lateral SMA50', 'chão', 'chão SMA10', 'chão SMA20', 'chão SMA50'];
export const NSLOT = 8;
export const isSide = (slot: number) => slot < 4;

// Tipos de alarme (bits)
export const A_ZERO = 1, A_CONTAGEM = 2, A_INTEIRA = 4, A_BORDA = 8, A_SALTO = 16, A_MATERIAL = 32, A_COR = 64,
  A_REMONTA = 128, A_LOTE = 256, A_TRACEJADO = 512, A_AUSENTE = 1024;
export const NTYPE = 11;
export const TYPE_NAMES = ['contagem 0', 'contagem <50% ou >150%', 'linha inteira (sem recorte)', 'borda fora (>2 velas)', 'salto de contagem >10%',
  'material trocado', 'cor por vértice trocou', 'remontagem', 'lote geometria ≠ transformação', 'tracejado sem distâncias', 'não desenhada'];

export const TH_BORDA_VELAS = 2;
const ALARM_CAP = 600;
const MARK_CAP = 300;
const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');

/** Primeiro índice i em [0, n) com x(i) >= x (pontos em ordem crescente de X). */
export function firstAtOrAbove(pts: ArrayLike<number>, n: number, x: number): number {
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid * 3] < x) lo = mid + 1; else hi = mid; }
  return lo;
}

/** O que o app faz no recorte da lateral (SceneContent.clipLineToX), escrito aqui para recalcular sobre outro array. */
export function expectedRange(pts: ArrayLike<number>, n: number, xLo: number, xHi: number, out: Float64Array): void {
  const a = Math.max(0, firstAtOrAbove(pts, n, xLo) - 1);
  const b = Math.min(n - 1, firstAtOrAbove(pts, n, xHi));
  out[0] = a; out[1] = Math.max(0, b - a + 1);
}

export class SideProbe {
  // entradas por quadro (o hook preenche antes do render)
  now = 0; prevNow = 0; mode = 4; frameNo = 0;
  xLoWall = 0; xHiWall = 0; xLoMain = 0; xHiMain = 0; stepWall = 1; stepMain = 1;
  loteWall = 0; loteMain = 0;
  /** altura da parede (para medir o tamanho dos segmentos que entram/saem nas pontas, em % da altura) */
  H = 1200;
  /** pontos esperados na GPU por linha (do descritor do lote que está na GPU) */
  readonly nExp = new Float64Array(NSLOT);
  /** pontos válidos da cópia da GPU (contagem do último upload, vista pelo recorte; -1 = desconhecida) */
  readonly nGpuValid = new Float64Array(NSLOT).fill(-1);
  /** a linha deveria estar sendo desenhada neste quadro? */
  readonly want = new Uint8Array(NSLOT);
  /** está em troca de lote neste quadro (salto de contagem é esperado) */
  loteSwitch = false;

  // estado por linha
  readonly obj: any[] = new Array(NSLOT).fill(null);
  readonly mat: any[] = new Array(NSLOT).fill(null);
  readonly vc = new Int8Array(NSLOT).fill(-1);
  readonly lastCount = new Float64Array(NSLOT).fill(-1);
  readonly posVer = new Float64Array(NSLOT).fill(-1);
  readonly colVer = new Float64Array(NSLOT).fill(-1);
  readonly geomLote = new Float64Array(NSLOT).fill(NaN);
  readonly drawnFrame = new Float64Array(NSLOT).fill(-1);
  readonly mounts = new Float64Array(NSLOT);
  readonly frames = new Float64Array(NSLOT * NSM);        // quadros desenhados por linha e modo
  readonly modeFrames = new Float64Array(NSM);
  readonly modeTime = new Float64Array(NSM);
  readonly byType = new Float64Array(2 * NSM * NTYPE);     // [lateral|chão][modo][tipo]
  readonly anyByKind = new Float64Array(2 * NSM);
  maxBordaVelas = 0;
  // Pontas da lateral (L9): o recorte por drawRange é DISCRETO (ponto a ponto), enquanto o chão é cortado por planos
  // (contínuo). A cada quadro em que o início/fim do recorte muda, mede o comprimento (Y/Z, % da altura) dos
  // segmentos que entraram ou saíram nas pontas: é o "pedaço que aparece/some" de uma vez.
  readonly prevStart = new Float64Array(4).fill(-1);
  readonly prevEnd = new Float64Array(4).fill(-1);
  readonly popFrames = new Float64Array(NSM);
  readonly popOver5 = new Float64Array(NSM);
  readonly popSum = new Float64Array(NSM);
  readonly popMax = new Float64Array(NSM);
  // L8 (testemunha): o recorte ANTIGO (busca no array do React, U.*) contra o recorte sobre a cópia da GPU.
  // Depois do P2 o desenho usa a cópia da GPU; esta medida mostra quanto o recorte antigo erraria no mesmo quadro.
  readonly l8Frames = new Float64Array(NSM);     // quadros (lateral, qualquer linha) com erro do recorte antigo > 2 velas
  readonly l8Max = new Float64Array(NSM);
  private l8Frame = -1;
  ovN = 0; ovSum = 0; ovMax = 0;
  private readonly tmp = new Float64Array(2);
  private alarmFrameT = -1; private prevT = 0;
  // Pontas VISÍVEIS da linha principal (P1): onde a linha começa/termina na tela contra onde deveria (o ponto da
  // curva exatamente na borda da janela). Sem corte contínuo a ponta visível é o vértice do drawRange (fora da
  // borda); com o corte por X no shader é o ponto da curva na janela que o shader recebe. "Salto" = quanto esse erro
  // muda de um quadro para o outro (Y/Z, % da altura): é o pedaço que aparece/some de uma vez.
  readonly visFrames = new Float64Array(NSM);     // quadros com a ponta (ideal ou visível) mudando
  readonly visSum = new Float64Array(NSM);
  readonly visMax = new Float64Array(NSM);
  readonly visOver5 = new Float64Array(NSM);
  readonly contFrames = new Float64Array(NSM);    // quadros com o corte contínuo ATIVO no programa da GPU
  readonly contOff = new Float64Array(NSM);       // quadros em que o corte contínuo estava pedido mas não ativo
  private popVer = -1;
  private readonly prevErr = new Float64Array(4);
  private readonly prevIdeal = new Float64Array(4);
  private hasPrevErr = false;
  private readonly ep = new Float64Array(2);

  // anel de alarmes
  alarmN = 0; private head = 0;
  aT = new Float64Array(ALARM_CAP); aSlot = new Uint8Array(ALARM_CAP); aType = new Uint16Array(ALARM_CAP); aMode = new Uint8Array(ALARM_CAP);
  aMask = new Uint32Array(ALARM_CAP); aCount = new Float64Array(ALARM_CAP); aExp = new Float64Array(ALARM_CAP); aStart = new Float64Array(ALARM_CAP);
  aExpStart = new Float64Array(ALARM_CAP); aBorda = new Float32Array(ALARM_CAP); aN = new Float64Array(ALARM_CAP);
  aPosVer = new Float64Array(ALARM_CAP); aColVer = new Float64Array(ALARM_CAP);

  // marcas "Marcar lateral"
  markN = 0; private markHead = 0;
  mT = new Float64Array(MARK_CAP); mMode = new Uint8Array(MARK_CAP); mPos = new Float64Array(MARK_CAP);
  t0 = 0;

  /** Início do quadro (useFrame da cena, depois do recorte e antes do render). */
  frame(now: number): void {
    // linhas que deveriam ter sido desenhadas no quadro anterior e não foram
    if (this.prevNow > 0) {
      for (let s = 0; s < NSLOT; s++) {
        if (this.want[s] && this.obj[s] && this.drawnFrame[s] !== this.frameNo) this.alarm(s, A_AUSENTE, NaN, NaN, NaN, NaN, NaN, 0);
      }
    }
    const dt = this.prevNow > 0 ? now - this.prevNow : 0;
    if (this.t0 === 0) this.t0 = now;
    this.prevT = this.now; this.prevNow = this.now = now; this.frameNo++;
    if (dt > 0 && dt < 5000) { this.modeFrames[this.mode]++; this.modeTime[this.mode] += dt; }
  }

  /** Nova instância (objeto Line) vista para a linha: conta montagem; troca de objeto = remontagem. */
  attach(slot: number, o: any): boolean {
    if (this.obj[slot] === o) return false;
    const was = this.obj[slot] !== null;
    this.obj[slot] = o; this.mounts[slot]++;
    if (was) this.alarm(slot, A_REMONTA, NaN, NaN, NaN, NaN, NaN, 0);
    return true;
  }

  /** onBeforeRender da linha: o que vai para a GPU agora. */
  draw(slot: number, geometry: any, material: any, renderer?: any): void {
    const a = performance.now();
    this.drawnFrame[slot] = this.frameNo;
    this.frames[slot * NSM + this.mode]++;
    const pos = geometry.attributes.position;
    const dr = geometry.drawRange;
    const n = this.nExp[slot];
    const nGpu = pos ? pos.count : 0;
    const start = dr.start;
    let count = dr.count; if (!isFinite(count)) count = nGpu - start;
    let type = 0;

    // material e cor por vértice
    if (this.mat[slot] !== null && this.mat[slot] !== material) type |= A_MATERIAL;
    this.mat[slot] = material;
    const vcNow = material && material.vertexColors ? 1 : 0;
    if (this.vc[slot] >= 0 && this.vc[slot] !== vcNow) type |= A_COR;
    this.vc[slot] = vcNow;
    if (material && material.isLineDashedMaterial && !geometry.attributes.lineDistance) type |= A_TRACEJADO;

    // lote da geometria: anotado quando a posição muda de versão (upload)
    const pv = pos ? pos.version : -1;
    const loteNow = isSide(slot) ? (slot === 0 ? this.loteWall : this.loteMain) : (slot === 4 ? this.loteWall : this.loteMain);
    if (pv !== this.posVer[slot]) { this.posVer[slot] = pv; this.geomLote[slot] = loteNow; }
    else if (this.geomLote[slot] === this.geomLote[slot] && this.geomLote[slot] !== loteNow) type |= A_LOTE;
    const col = geometry.attributes.color; this.colVer[slot] = col ? col.version : -1;

    let exp = n, expStart = 0, borda = 0;
    if (count <= 0 && n >= 2) type |= A_ZERO;
    if (isSide(slot)) {
      // esperado: mesma janela visível, recalculada sobre a CÓPIA da GPU (array do atributo)
      const nv = this.nGpuValid[slot];
    const useN = nv >= 0 ? Math.min(nv, nGpu) : Math.min(n, nGpu);
      if (pos && useN >= 2) {
        const isWall = slot === 0;
        expectedRange(pos.array, useN, isWall ? this.xLoWall : this.xLoMain, isWall ? this.xHiWall : this.xHiMain, this.tmp);
        expStart = this.tmp[0]; exp = this.tmp[1];
        const step = isWall ? this.stepWall : this.stepMain;
        const arr = pos.array;
        const sEnd = Math.min(useN - 1, start + count - 1), eEnd = expStart + exp - 1;
        const s0 = Math.min(useN - 1, Math.max(0, start));
        const bS = Math.abs(arr[s0 * 3] - arr[expStart * 3]) / step;
        const bE = sEnd >= 0 && eEnd >= 0 ? Math.abs(arr[Math.max(0, sEnd) * 3] - arr[eEnd * 3]) / step : 0;
        borda = bS > bE ? bS : bE;
        if (borda > this.maxBordaVelas) this.maxBordaVelas = borda;
        if (borda > TH_BORDA_VELAS) type |= A_BORDA;
        if (count > 0 && exp > 0 && (count < exp * 0.5 || count > exp * 1.5)) type |= A_CONTAGEM;
        if (exp < useN * 0.9 && count >= nGpu - start && count >= useN) type |= A_INTEIRA;
        if (slot === 0) {
          // upload novo = índices renumerados (troca de lote): não é pedaço entrando/saindo, recomeça a contagem
          if (pv !== this.popVer) { this.popVer = pv; this.prevStart[0] = -1; }
          this.measurePop(arr, useN, start, start + count - 1);
          this.measureEnds(arr, useN, start, Math.min(useN - 1, start + count - 1), material, renderer);
        }
      }
    } else if (n > 0 && count > 0 && count < n * 0.5) type |= A_CONTAGEM;
    const prev = this.lastCount[slot];
    if (prev > 0 && !this.loteSwitch && Math.abs(count - prev) > prev * 0.1) type |= A_SALTO;
    this.lastCount[slot] = count;
    if (type) this.alarm(slot, type, count, exp, start, expStart, borda, n);
    const ms = performance.now() - a; this.ovN++; this.ovSum += ms; if (ms > this.ovMax) this.ovMax = ms;
  }

  /**
   * Testemunha de L8 (chamada pelo hook antes do render): onde o recorte ANTIGO cortaria (busca no array do React,
   * uArr/uN) contra o recorte correto (busca na cópia da GPU, gArr/gN), medido em velas sobre a cópia da GPU.
   */
  witnessL8(slot: number, uArr: ArrayLike<number>, uN: number, gArr: ArrayLike<number>, gN: number): number {
    if (uN < 2 || gN < 2) return 0;
    // janela lida dos campos (passar X fracionário como argumento criaria números em caixa a cada quadro)
    const wall = slot === 0, xLo = wall ? this.xLoWall : this.xLoMain, xHi = wall ? this.xHiWall : this.xHiMain;
    const step = wall ? this.stepWall : this.stepMain;
    const t = this.tmp;
    expectedRange(uArr, uN, xLo, xHi, t); const ua = t[0], ub = t[0] + t[1] - 1;
    expectedRange(gArr, gN, xLo, xHi, t); const ga = t[0], gb = t[0] + t[1] - 1;
    const c = gN - 1;
    const e1 = Math.abs(gArr[(ua < c ? ua : c) * 3] - gArr[ga * 3]) / step;
    const e2 = Math.abs(gArr[(ub < c ? ub : c) * 3] - gArr[gb * 3]) / step;
    const e = e1 > e2 ? e1 : e2, m = this.mode;
    if (e > this.l8Max[m]) this.l8Max[m] = e;
    if (e > TH_BORDA_VELAS && this.l8Frame !== this.frameNo) { this.l8Frame = this.frameNo; this.l8Frames[m]++; }
    return e;
  }

  /** Comprimento (Y/Z) dos segmentos que entraram/saíram nas duas pontas desde o quadro anterior, em % de H. */
  private measurePop(arr: ArrayLike<number>, n: number, s: number, e: number): void {
    const ps = this.prevStart[0], pe = this.prevEnd[0];
    this.prevStart[0] = s; this.prevEnd[0] = e;
    if (ps < 0 || (ps === s && pe === e)) return;
    let len = 0;
    const a0 = s < ps ? s : ps, a1 = s < ps ? ps : s;            // segmentos entre o início antigo e o novo
    for (let i = a0; i < a1 && i < a0 + 40; i++) len += this.seg(arr, n, i);
    const b0 = e < pe ? e : pe, b1 = e < pe ? pe : e;            // entre o fim antigo e o novo
    for (let i = b0; i < b1 && i < b0 + 40; i++) len += this.seg(arr, n, i);
    const pct = (len / (this.H || 1)) * 100, m = this.mode;
    this.popFrames[m]++; this.popSum[m] += pct; if (pct > this.popMax[m]) this.popMax[m] = pct; if (pct > 5) this.popOver5[m]++;
  }

  /** Ponto (Y, Z) da curva da GPU em X = x (interpolado entre os dois vértices vizinhos; fora dos dados, a ponta). */
  edgePoint(arr: ArrayLike<number>, n: number, x: number, out: Float64Array): void {
    const i = firstAtOrAbove(arr, n, x);
    if (i <= 0) { out[0] = arr[1]; out[1] = arr[2]; return; }
    if (i >= n) { out[0] = arr[(n - 1) * 3 + 1]; out[1] = arr[(n - 1) * 3 + 2]; return; }
    const x0 = arr[(i - 1) * 3], x1 = arr[i * 3], dx = x1 - x0;
    const t = dx > 0 ? (x - x0) / dx : 1;
    out[0] = arr[(i - 1) * 3 + 1] + (arr[i * 3 + 1] - arr[(i - 1) * 3 + 1]) * t;
    out[1] = arr[(i - 1) * 3 + 2] + (arr[i * 3 + 2] - arr[(i - 1) * 3 + 2]) * t;
  }

  /** Erro das pontas visíveis contra as ideais e o salto desse erro entre quadros (linha principal da lateral). */
  measureEnds(arr: ArrayLike<number>, n: number, s: number, e: number, material: any, renderer: any): void {
    const xLo = this.xLoWall, xHi = this.xHiWall, ep = this.ep, m = this.mode;
    // o corte contínuo está ativo no programa que a GPU vai usar? (uniforms do material compilado = os da linha)
    const st = material && material.userData ? material.userData.sideXClip : null;
    let live = false;
    if (st) {
      const props = renderer && renderer.properties ? renderer.properties.get(material) : null;
      live = !!(st.injected && props && props.uniforms && props.uniforms.uSideXLo === st.lo);
      if (live) this.contFrames[m]++; else this.contOff[m]++;
    }
    // ideal: a curva exatamente na borda da janela
    this.edgePoint(arr, n, xLo, ep); const iSy = ep[0], iSz = ep[1];
    this.edgePoint(arr, n, xHi, ep); const iEy = ep[0], iEz = ep[1];
    // visível
    let vSy: number, vSz: number, vEy: number, vEz: number;
    if (live) {
      const lo = st.lo.value, hi = st.hi.value, xs = arr[s * 3], xe = arr[e * 3];
      this.edgePoint(arr, n, lo > xs ? lo : xs, ep); vSy = ep[0]; vSz = ep[1];
      this.edgePoint(arr, n, hi < xe ? hi : xe, ep); vEy = ep[0]; vEz = ep[1];
    } else { vSy = arr[s * 3 + 1]; vSz = arr[s * 3 + 2]; vEy = arr[e * 3 + 1]; vEz = arr[e * 3 + 2]; }
    const E = this.prevErr, I = this.prevIdeal;
    const eSy = vSy - iSy, eSz = vSz - iSz, eEy = vEy - iEy, eEz = vEz - iEz;
    if (this.hasPrevErr) {
      const moved = iSy !== I[0] || iSz !== I[1] || iEy !== I[2] || iEz !== I[3] || eSy !== E[0] || eSz !== E[1] || eEy !== E[2] || eEz !== E[3];
      if (moved) {
        const a1 = eSy - E[0], a2 = eSz - E[1], b1 = eEy - E[2], b2 = eEz - E[3];
        const pct = ((Math.sqrt(a1 * a1 + a2 * a2) + Math.sqrt(b1 * b1 + b2 * b2)) / (this.H || 1)) * 100;
        this.visFrames[m]++; this.visSum[m] += pct; if (pct > this.visMax[m]) this.visMax[m] = pct; if (pct > 5) this.visOver5[m]++;
      }
    }
    E[0] = eSy; E[1] = eSz; E[2] = eEy; E[3] = eEz; I[0] = iSy; I[1] = iSz; I[2] = iEy; I[3] = iEz; this.hasPrevErr = true;
  }

  private seg(arr: ArrayLike<number>, n: number, i: number): number {
    if (i < 0 || i + 1 >= n) return 0;
    const dy = arr[(i + 1) * 3 + 1] - arr[i * 3 + 1], dz = arr[(i + 1) * 3 + 2] - arr[i * 3 + 2];
    return Math.sqrt(dy * dy + dz * dz);
  }

  private alarm(slot: number, type: number, count: number, exp: number, start: number, expStart: number, borda: number, n: number): void {
    if (jank && this.now < jank.bgUntil) return; // P1b T6(d): 2 s depois da volta do segundo plano não contam
    const k = isSide(slot) ? 0 : 1;
    for (let b = 0; b < NTYPE; b++) if (type & (1 << b)) this.byType[(k * NSM + this.mode) * NTYPE + b]++;
    // conta "quadros com alarme" por tipo de linha uma vez por quadro
    const key = this.frameNo * 2 + k;
    if (this.alarmFrameT !== key) { this.alarmFrameT = key; this.anyByKind[k * NSM + this.mode]++; }
    const i = this.head;
    this.aT[i] = this.now; this.aSlot[i] = slot; this.aType[i] = type; this.aMode[i] = this.mode;
    this.aMask[i] = jank ? jank.maskSince(this.prevT || this.now - 20) : 0;
    this.aCount[i] = count; this.aExp[i] = exp; this.aStart[i] = start; this.aExpStart[i] = expStart; this.aBorda[i] = borda; this.aN[i] = n;
    this.aPosVer[i] = this.posVer[slot]; this.aColVer[i] = this.colVer[slot];
    this.head = (i + 1) % ALARM_CAP; this.alarmN++;
  }

  mark(now: number, pos: number): void {
    const i = this.markHead;
    this.mT[i] = now; this.mMode[i] = this.mode; this.mPos[i] = pos;
    this.markHead = (i + 1) % MARK_CAP; this.markN++;
  }

  // ---------------------------------------------------------------- relatório (fora do caminho quente)
  get alarmSize() { return this.alarmN < ALARM_CAP ? this.alarmN : ALARM_CAP; }
  alarmAt(k: number) { return (this.head - this.alarmSize + k + ALARM_CAP * 2) % ALARM_CAP; }

  /** Fração do tempo com alarme da lateral em ±halfMs (nível de acaso para uma marca aleatória). */
  chanceCoverage(halfMs: number, s0: number, s1: number, sideOnly = true): number {
    if (s1 <= s0) return 0;
    let covered = 0, cS = -Infinity, cE = -Infinity;
    for (let k = 0; k < this.alarmSize; k++) {
      const i = this.alarmAt(k); if (sideOnly && !isSide(this.aSlot[i])) continue;
      const s = Math.max(s0, this.aT[i] - halfMs), e = Math.min(s1, this.aT[i] + halfMs);
      if (s > cE) { if (cE > cS) covered += cE - cS; cS = s; cE = e; } else if (e > cE) cE = e;
    }
    if (cE > cS) covered += cE - cS;
    return covered / (s1 - s0);
  }

  typesOf(t: number): string { const o: string[] = []; for (let b = 0; b < NTYPE; b++) if (t & (1 << b)) o.push(TYPE_NAMES[b]); return o.join(', '); }

  /** Seção "LATERAL" do "Copiar log" (full) ou do "Copiar resumo" (compacto). */
  report(t0Abs: number, compact = false): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sess = this.prevNow - this.t0;
    L.push('=== LATERAL (Preço x Força) — controle: CHÃO ===');
    if (!compact) L.push('LEGENDA: medido imediatamente antes do desenho de cada linha. "Esperado" = recorte recalculado sobre a cópia que está na GPU, com a mesma janela visível. Alarmes: ' + TYPE_NAMES.join(' | ') + '.');
    const tot = (k: number) => { let a = 0; for (let m = 0; m < NSM; m++) a += this.anyByKind[k * NSM + m]; return a; };
    L.push(`sessão: ${f(sess / 1000, 0)}s | quadros com alarme: lateral ${tot(0)} (${f(tot(0) / Math.max(1e-9, sess / 60000), 1)}/min) | chão ${tot(1)} (${f(tot(1) / Math.max(1e-9, sess / 60000), 1)}/min) | maior erro de borda da lateral ${f(this.maxBordaVelas, 1)} velas | overhead ${f((this.ovSum / Math.max(1, this.ovN)) * 1000, 1)} µs/linha, máx ${f(this.ovMax, 3)} ms`);
    if (!compact) L.push(`montagens por linha: ${SLOT_NAMES.map((n, s) => `${n} ${this.mounts[s]}`).join(' | ')}`);
    const modes: number[] = []; for (let m = 0; m < NSM; m++) if (this.modeFrames[m] > 0 || this.modeTime[m] > 0) modes.push(m);
    if (compact) {
      // resumo: uma linha por modo (alarmes da lateral + salto visível das pontas); o chão fica só no total acima
      L.push('--- LATERAL por modo: tempo | quadros com alarme (tipos) | salto VISÍVEL das pontas média/máx (meta < 0,5% / < 5%) | corte contínuo ativo ---');
      for (const m of modes) {
        const parts: string[] = [];
        for (let b = 0; b < NTYPE; b++) { const c = this.byType[m * NTYPE + b]; if (c > 0) parts.push(`${TYPE_NAMES[b]} ${c}`); }
        const vf = this.visFrames[m];
        L.push(`${SMODE_NAMES[m]} | ${f(this.modeTime[m] / 1000, 0)}s | alarmes ${this.anyByKind[m]}${parts.length ? ` (${parts.join(', ')})` : ''} | salto ${f(vf ? this.visSum[m] / vf : NaN, 2)}%/${f(this.visMax[m], 2)}% (>5%: ${this.visOver5[m]}) | contínuo ${this.contFrames[m]}/${this.contFrames[m] + this.contOff[m]}`);
      }
    }
    for (let k = 0; k < 2 && !compact; k++) {
      L.push(`--- ${k === 0 ? 'LATERAL' : 'CHÃO (controle)'}: alarmes por tipo e por modo (quadros/min) ---`);
      for (const m of modes) {
        if (this.modeTime[m] <= 0) continue;
        const mn = this.modeTime[m] / 60000;
        const parts: string[] = [];
        for (let b = 0; b < NTYPE; b++) { const c = this.byType[(k * NSM + m) * NTYPE + b]; if (c > 0) parts.push(`${TYPE_NAMES[b]} ${c}`); }
        L.push(`${SMODE_NAMES[m]} | ${f(this.modeTime[m] / 1000, 0)}s | quadros com alarme ${this.anyByKind[k * NSM + m]} (${f(this.anyByKind[k * NSM + m] / Math.max(1e-9, mn), 1)}/min) | ${parts.join(', ') || 'nenhum'}`);
      }
    }
    // pontas visíveis (P1): a métrica principal
    if (!compact) L.push('--- LATERAL: salto VISÍVEL das pontas (ponta na tela contra a curva na borda; variação por quadro, % da altura; meta < 0,5% média, < 5% máx) ---');
    for (let q = 0; q < modes.length && !compact; q++) {
      const m = modes[q], vf = this.visFrames[m];
      L.push(`${SMODE_NAMES[m]} | quadros ${vf} | média ${f(vf ? this.visSum[m] / vf : NaN, 2)}% | máx ${f(this.visMax[m], 2)}% | > 5%: ${this.visOver5[m]} | corte contínuo ativo ${this.contFrames[m]}, inativo ${this.contOff[m]}`);
    }
    if (!compact) {
      L.push('--- LATERAL: segmentos que entram/saem do drawRange por quadro (sem o corte contínuo aparecem inteiros; com ele o shader apara na borda) ---');
      for (const m of modes) {
        const pf = this.popFrames[m];
        L.push(`${SMODE_NAMES[m]} | quadros com ponta mudando ${pf} de ${this.modeFrames[m]} | média ${f(pf ? this.popSum[m] / pf : NaN, 1)}% da altura | máx ${f(this.popMax[m], 1)}% | quadros > 5% da altura: ${this.popOver5[m]}`);
      }
      L.push(`cor por vértice no material agora (lateral e chão principais sem cor por vértice aparecem BRANCAS; as SMAs do chão são de cor sólida por projeto): ${SLOT_NAMES.map((nm, s) => `${nm} ${this.vc[s] < 0 ? '—' : this.vc[s] ? 'sim' : 'não'}`).join(' | ')}`);
    }
    // marcas
    const nM = Math.min(this.markN, MARK_CAP);
    const s0 = this.t0, s1 = this.prevNow;
    let h100 = 0, h400 = 0; const lines: string[] = [];
    for (let k = 0; k < nM; k++) {
      const i = (this.markHead - nM + k + MARK_CAP * 2) % MARK_CAP, tm = this.mT[i];
      let best = Infinity, bi = -1;
      for (let q = 0; q < this.alarmSize; q++) { const j = this.alarmAt(q); if (!isSide(this.aSlot[j])) continue; const d = Math.abs(this.aT[j] - tm); if (d < best) { best = d; bi = j; } }
      if (best <= 100) h100++; if (best <= 400) h400++;
      lines.push(`t=${rel(tm)} ${SMODE_NAMES[this.mMode[i]]} pos=${f(this.mPos[i], 0)} → ${bi >= 0 ? `alarme da lateral a ${f(best, 0)}ms (${SLOT_NAMES[this.aSlot[bi]]}: ${this.typesOf(this.aType[bi])})` : 'nenhum alarme'}`);
    }
    L.push(`--- marcas "Marcar lateral": ${nM} | alarme da lateral em ±100ms: ${h100} (${nM ? f((h100 / nM) * 100, 0) : '—'}%) contra acaso ${f(this.chanceCoverage(100, s0, s1) * 100, 0)}% | em ±400ms: ${h400} (${nM ? f((h400 / nM) * 100, 0) : '—'}%) contra acaso ${f(this.chanceCoverage(400, s0, s1) * 100, 0)}% ---`);
    for (const l of lines.slice(compact ? -6 : -30)) L.push(l);
    // L8: testemunha do recorte antigo (array do React) contra o recorte sobre a cópia da GPU
    if (compact) {
      const p8: string[] = [];
      for (let m = 0; m < NSM; m++) if (this.modeFrames[m] > 0) p8.push(`${SMODE_NAMES[m]} ${this.l8Frames[m]} (máx ${f(this.l8Max[m], 0)})`);
      L.push(`--- L8 (recorte antigo sobre o array do React erraria > 2 velas; o desenho usa a cópia da GPU): ${p8.join(' | ')} ---`);
    } else {
      L.push('--- LATERAL: recorte antigo (array do React) contra a cópia da GPU — causa L8; o desenho usa a cópia da GPU ---');
      for (let m = 0; m < NSM; m++) if (this.modeFrames[m] > 0) L.push(`${SMODE_NAMES[m]} | quadros em que o recorte antigo erraria > 2 velas: ${this.l8Frames[m]} | máx ${f(this.l8Max[m], 1)} velas`);
    }
    // últimos alarmes
    const N = compact ? 8 : 30;
    L.push(`--- últimos ${Math.min(N, this.alarmSize)} alarmes ---`);
    for (let k = Math.max(0, this.alarmSize - N); k < this.alarmSize; k++) {
      const i = this.alarmAt(k);
      L.push(`t=${rel(this.aT[i])} ${SMODE_NAMES[this.aMode[i]]} ${SLOT_NAMES[this.aSlot[i]]}: ${this.typesOf(this.aType[i])} | drawRange ${f(this.aStart[i], 0)}+${f(this.aCount[i], 0)} esperado ${f(this.aExpStart[i], 0)}+${f(this.aExp[i], 0)} de ${f(this.aN[i], 0)} | borda ${f(this.aBorda[i], 1)} velas | versões pos/cor ${this.aPosVer[i]}/${this.aColVer[i]} [${jank ? jank.labelsOf(this.aMask[i]) : ''}]`);
    }
    return L;
  }
}

export const side: SideProbe | null = DBG_ENABLED ? new SideProbe() : null;
export { NEV, EV_NAMES };
